import React, { useState, useEffect } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SxProps,
  Theme,
  SelectChangeEvent,
} from '@mui/material'
import OptionMenuPaper from '../cells/OptionMenuPaper'
import { useLeadStatuses } from '../../../hooks/leads/useLeadStatuses'
import AddCustomLabelDialog from './AddCustomLabelDialog'

export interface LeadStatusSelectProps {
  smbId?: string
  value: string
  onChange: (status: string) => void
  customStatuses?: string[]
  setCustomStatuses?: React.Dispatch<React.SetStateAction<string[]>>
  onStatusUpdated?: (res?: any, createdStatus?: string) => void
  label?: string
  disabled?: boolean
  fullWidth?: boolean
  sx?: SxProps<Theme>
  size?: 'small' | 'medium'
  id?: string
  helperText?: React.ReactNode
  FormHelperTextProps?: any
}

/**
 * Reusable Lead Status Select component.
 * Centralizes status list rendering, lazy loading, and custom status creation/editing dialog integration.
 */
export const LeadStatusSelect: React.FC<LeadStatusSelectProps> = ({
  smbId,
  value,
  onChange,
  customStatuses,
  setCustomStatuses,
  onStatusUpdated,
  label = 'Lead Status',
  disabled = false,
  fullWidth = true,
  sx,
  size,
  id = 'lead-status-select',
  helperText,
  FormHelperTextProps,
}) => {
  const [isAddCustomLabelOpen, setIsAddCustomLabelOpen] = useState(false)
  const [selectOpen, setSelectOpen] = useState(false)

  const {
    statuses: activeStatuses,
    fetchStatuses,
    handleStatusComplete,
  } = useLeadStatuses(smbId, customStatuses, setCustomStatuses)

  useEffect(() => {
    if (smbId && !customStatuses?.length && activeStatuses.length === 0) {
      fetchStatuses()
    }
  }, [smbId, customStatuses, activeStatuses.length, fetchStatuses])

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    onChange(e.target.value)
  }

  const allStatuses = value ? Array.from(new Set([...activeStatuses, value])) : activeStatuses
  const sortedStatuses = [...allStatuses]

  return (
    <>
      <FormControl fullWidth={fullWidth} sx={sx} size={size} disabled={disabled}>
        <InputLabel id={`${id}-label`}>{label}</InputLabel>
        <Select
          labelId={`${id}-label`}
          id={id}
          value={value}
          label={label}
          disabled={disabled}
          open={selectOpen}
          onOpen={() => {
            setSelectOpen(true)
            if (smbId && !customStatuses?.length) {
              fetchStatuses()
            }
          }}
          onClose={() => setSelectOpen(false)}
          onChange={handleSelectChange}
          MenuProps={{
            PaperProps: {
              component: OptionMenuPaper,
              onActionClick: () => {
                setSelectOpen(false)
                setIsAddCustomLabelOpen(true)
              },
              actionText: 'Create Status',
              style: {
                maxHeight: 230,
              },
            } as any,
            MenuListProps: {
              sx: {
                pb: 0,
              },
            },
          }}
        >
          {sortedStatuses.map((st) => (
            <MenuItem key={st} value={st}>
              {st}
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <FormHelperText {...FormHelperTextProps}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>

      {smbId && (
        <AddCustomLabelDialog
          open={isAddCustomLabelOpen}
          onClose={() => setIsAddCustomLabelOpen(false)}
          onComplete={async (res, createdStatus) => {
            await handleStatusComplete(res, createdStatus, value, onChange, setCustomStatuses)
            if (onStatusUpdated) {
              onStatusUpdated(res, createdStatus)
            }
          }}
          smbId={smbId}
          customStatuses={activeStatuses}
        />
      )}
    </>
  )
}

export default LeadStatusSelect
